import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import Pubnub, { generateUUID } from 'pubnub';
import { ReactElement, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isAiCase } from '../../components/layout/slideout/communications-widget/utils/is-ai-case';
import CallHistoryService from '../../services/call-history/call-history-service';
import { CommunicationType } from '../../services/inbox-service/types';
import { Message, MessageStatus, timeTokenToNumber } from '../../services/pubnub-service';
import { TemplateTransportType } from '../../types/communication/transport-type';
import { PatientCaseWithTimetoken, useLeadsManagement } from '../use-leads-management/use-leads-management';
import { useLocations } from '../use-locations';
import { usePubnub } from '../use-pubnub/use-pubnub';
import { CommunicationsQuery, LegacySearchParams } from './data/types';
import { useCommuicationData } from './data/use-communication-data';
import { mapCallNotificationToCallHistoryItem } from './mappers/call';
import { AutomatedMessage } from './types/automated-message';
import { CallHistoryItem } from './types/comms-call-types';
import { ManagedCommItem } from './types/managed-communication';
import useCommsFilter from './use-communications-filter';

interface ICommuncationsContext {
  data: (Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken)[];
  automatedMessages: AutomatedMessage[];
  isLoading: boolean;
  hasMore: boolean;
  patientPhoneNumbers: string[];
  patientId?: number | null;
  setSearchByPhoneNumbers: (patientPhoneNumbers: string[]) => void;
  loadMore: () => Promise<void>;
  addOrUpdateMessage: (message: Message) => void;
  downloadFax: (faxId: string) => void;
  listenToPhoneNumbers: (phoneNumbers: string[]) => () => void;
  setPatientId: (patientId: number | null) => void;
}

function isMessage(item: Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken): item is Message {
  return (item as Message).body !== undefined;
}

const CommuncationsContext = createContext<ICommuncationsContext | null>(null);

export const useCommunications = (): ICommuncationsContext => {
  const context = useContext(CommuncationsContext);
  if (!context) throw new Error('useCommunications must be used within an CommuncationsContext');

  return context;
};

export const CommunicationsProvider = ({ children }: React.PropsWithChildren<object>): ReactElement | null => {
  const { listen } = useNotifications();
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [_patientPhoneNumbers, setPatientPhoneNumbers] = useState<string[]>(null!);
  const [patientId, setPatientId] = useState<number | null>(null);
  const [query, setQuery] = useState<CommunicationsQuery>({});
  const [data, setData] = useState<(Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken)[]>([]);
  const [automatedMessages, setAutomatedMessages] = useState<AutomatedMessage[]>([]);
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { client, mailboxes } = usePubnub();
  const { commsFilter } = useCommsFilter();
  const { selectedLocationWithStatus } = useLocations();
  const [multiplePhoneNumbersSearch, setMultiplePhoneNumbersSearch] = useState<{ [id: string]: string[] }>({});

  const patientPhoneNumbers = useMemo(() => {
    const multiplePhonesArray = Object.values(multiplePhoneNumbersSearch).flat().filter(Boolean);
    const allPhones = [...(_patientPhoneNumbers || []), ...multiplePhonesArray];

    return allPhones.reduce((acc, phone) => (acc.includes(phone) ? acc : [...acc, phone]), [] as string[]);
  }, [_patientPhoneNumbers, multiplePhoneNumbersSearch]);

  const { getMessageHistory, getCallHistory, getManagedCommunicationsHistory } = useCommuicationData();
  const callHistoryApi = useMemo(() => CallHistoryService.getInstance(config, oidcService), [config, oidcService]);
  const { getLeadsHistory } = useLeadsManagement();

  const [legacyParams, setLegacyParams] = useState<LegacySearchParams>({
    pageNumber: 1,
    pubnubComplete: false,
    legacyComplete: false,
    legacyData: []
  });

  const listenToPhoneNumbers = useCallback((phoneNumbers: string[]) => {
    const listenPhones = { [generateUUID()]: phoneNumbers };

    setMultiplePhoneNumbersSearch((old) => {
      return {
        ...old,
        ...listenPhones
      };
    });

    return () => {
      setMultiplePhoneNumbersSearch((old) => {
        const newPhones = { ...old };
        delete newPhones[Object.keys(listenPhones)[0]];
        return newPhones;
      });
    };
  }, []);

  // set initial search
  const setSearchByPhoneNumbers = useCallback(
    (phoneNumbers: string[]) => {
      if (mailboxes.length === 0) return;
      setPatientPhoneNumbers(phoneNumbers);
    },
    [mailboxes]
  );

  const getAutomatedMessages = useCallback((messages: Message[], managedComms: ManagedCommItem[]) => {
    const expectedReplies = ['c', 'p'];
    const result: AutomatedMessage[] = [];
    const managedCommsMap = new Map(
      managedComms.filter((x) => x.type === TemplateTransportType.Sms).map((x) => [x.communicationId.toLowerCase(), x])
    );
    const matchedCommIds = new Set<string>();

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];

      if (message.communicationId && managedCommsMap.has(message.communicationId.toLowerCase())) {
        const automatedMessageExists =
          result.findIndex((x) => x.message.communicationId?.toLowerCase() === message.communicationId?.toLowerCase()) >
          -1;

        if (automatedMessageExists) continue;

        const automatedMessage = { message, reply: null } as AutomatedMessage;
        const managedComm = managedCommsMap.get(message.communicationId.toLowerCase());

        if (managedComm && managedComm.answer) {
          automatedMessage.reply = {
            channelId: message.channelId,
            from: managedComm.sentTo,
            to: '',
            body: managedComm.answer,
            date: managedComm.date,
            timetoken: managedComm.timetoken + 1,
            status: 'received',
            messageType: 'incoming',
            isLegacy: false
          } as Message;
        } else if (i + 1 < messages.length && expectedReplies.includes(messages[i + 1].body.toLowerCase())) {
          automatedMessage.reply = messages[i + 1];
        }

        matchedCommIds.add(message.communicationId.toLowerCase());
        result.push(automatedMessage);
      }
    }

    // Add automated messages for managedComms with answers that weren't matched with PubNub messages
    for (const [commId, managedComm] of managedCommsMap) {
      if (!matchedCommIds.has(commId) && managedComm.answer) {
        const placeholderMessage: Message = {
          channelId: '',
          from: '',
          to: managedComm.sentTo,
          body: managedComm.note || managedComm.subject || '',
          date: managedComm.date,
          communicationId: managedComm.communicationId,
          timetoken: managedComm.timetoken,
          messageType: 'campaign',
          isLegacy: true
        };

        const automatedMessage: AutomatedMessage = {
          message: placeholderMessage,
          reply: {
            channelId: '',
            from: managedComm.sentTo,
            to: '',
            body: managedComm.answer,
            date: managedComm.date,
            timetoken: managedComm.timetoken + 1,
            status: 'received',
            messageType: 'incoming',
            isLegacy: true
          } as Message
        };

        result.push(automatedMessage);
      }
    }

    return result;
  }, []);

  // search for inbox history list
  const searchInboxHistory = useCallback(
    async (query: CommunicationsQuery, legacySearchParams: LegacySearchParams, start?: string) => {
      try {
        const { commType, deliveryType, phoneNumbers, patientId } = query;
        const request = { query, legacySearchParams, start };
        const hasPhoneNumbers = (phoneNumbers?.length ?? 0) > 0;

        setIsLoading(true);

        const loadMessageHistory = (!commType || [CommunicationType.Sms].includes(commType)) && hasPhoneNumbers;
        const loadCallHistory =
          (!commType || commType === CommunicationType.Phone) &&
          (!deliveryType || deliveryType == 'Direct') &&
          hasPhoneNumbers;

        const loadManagedComms =
          [CommunicationType.Email, CommunicationType.Sms, undefined, null].includes(commType) &&
          (!deliveryType || deliveryType == 'Automated') &&
          (hasPhoneNumbers || patientId);

        const [messageHistory, calls, managedComms, leadsHistory] = await Promise.all([
          loadMessageHistory ? getMessageHistory(request, data) : { messages: [], legacySearchParams },
          loadCallHistory ? getCallHistory(request) : [],
          loadManagedComms ? getManagedCommunicationsHistory(request) : [],
          getLeadsHistory(request)
        ]);

        const filteredLeadsHistory = leadsHistory.filter(
          (lead) => data.findIndex((item) => isAiCase(item) && item.caseId === lead.caseId) === -1
        );

        const calculatedAutomatedMessages = getAutomatedMessages(messageHistory.messages, managedComms);
        const messages =
          deliveryType === 'Automated'
            ? []
            : messageHistory.messages.filter(
                (x) =>
                  !calculatedAutomatedMessages
                    .flatMap((item) =>
                      [item.message.timetokenString, item.reply?.timetokenString].includes(x.timetokenString)
                    )
                    .some(Boolean)
              );

        const newData = [...calls, ...messages, ...managedComms, ...filteredLeadsHistory]
          .sort((a, b) => (a.timetoken || 0) - (b.timetoken || 0))
          .slice(-25);

        setData((prevData) => (start ? [...newData, ...prevData] : newData));
        setAutomatedMessages((prevData) =>
          start ? [...calculatedAutomatedMessages, ...prevData] : calculatedAutomatedMessages
        );
        setHasMore(newData.length > 0);
        setLegacyParams(messageHistory.legacySearchParams);
        setIsLoading(false);
      } catch (error) {
        console.error('Error searching inbox history.', error);
        setIsLoading(false);
      }
    },
    [client, data]
  );

  // set search by patient phone number and filter
  useEffect(() => {
    if (!selectedLocationWithStatus || selectedLocationWithStatus.status != 'Complete') return;
    if ((!patientPhoneNumbers || patientPhoneNumbers.length == 0) && patientId) return;

    const currMailboxes = mailboxes.filter((mailbox) => mailbox.locationId == selectedLocationWithStatus.location.id);

    let commType = undefined;

    switch (commsFilter.commType) {
      case 'SMS':
        commType = CommunicationType.Sms;
        break;
      case 'Phone':
        commType = CommunicationType.Phone;
        break;
      case 'Email':
        commType = CommunicationType.Email;
        break;
    }

    const query = patientPhoneNumbers
      ? {
          phoneNumbers: patientPhoneNumbers,
          patientId,
          mailboxes: currMailboxes,
          commType,
          deliveryType: commsFilter.deliveryType
        }
      : {};

    const legacySearchParams: LegacySearchParams = {
      pageNumber: 1,
      pubnubComplete: false,
      legacyComplete: false,
      legacyData: []
    };

    setData([]);
    setQuery(query);
    setLegacyParams(legacySearchParams);
    searchInboxHistory(query, legacySearchParams);
  }, [patientPhoneNumbers, patientId, mailboxes, commsFilter, selectedLocationWithStatus]);

  const addCallHistoryItem = useCallback(
    (item: CallHistoryItem) => {
      setData((prevData) => {
        const curIndex = prevData.findIndex((value: any) => value.id == item.id);
        if (curIndex == -1) {
          const newData = [...prevData];
          newData.push(item);
          return newData;
        } else {
          const newData = [...prevData];
          newData[curIndex] = item;
          return newData;
        }
      });
    },
    [data]
  );

  useEffect(() => {
    return listen('phone-call', async (event: any) => {
      if (event && event.data && event.data.type === 'call-ended') {
        if (patientPhoneNumbers.includes(event.data.to) || patientPhoneNumbers.includes(event.data.from)) {
          const item = mapCallNotificationToCallHistoryItem(event.data, event.locationid, event.masteraccountid);
          addCallHistoryItem(item);
        }
      }
    });
  }, [listen, addCallHistoryItem]);

  // load next page
  const loadMore = useCallback(async () => {
    if (query && hasMore) {
      const timeStart = data[0]?.timetoken ? (data[0].timetoken - 1).toString() : undefined;
      await searchInboxHistory(query, legacyParams, timeStart);
    }
  }, [query, data, hasMore, legacyParams, searchInboxHistory]);

  // listen for message update
  useEffect(() => {
    const { phoneNumbers, commType, mailboxes } = query;
    if (!client || !phoneNumbers || !mailboxes || commType === CommunicationType.Phone) return;

    const channelIds = phoneNumbers.flatMap((phoneNumber) =>
      mailboxes.map((item) => `${item.pubnubPrefix}.${phoneNumber}`)
    );

    const pubnubListener: Pubnub.ListenerParameters = {
      message: (msgObj: Pubnub.MessageEvent) => {
        if (!channelIds.includes(msgObj.channel)) return;

        const isInbound = msgObj.channel.split('.')[1] === msgObj.message.to;
        const status = isInbound && !msgObj.message.status ? 'sending' : msgObj.message.status;

        const message: Message = {
          ...msgObj.message,
          status: status,
          channelId: msgObj.channel,
          timetoken: timeTokenToNumber(msgObj.timetoken)
        };

        addOrUpdateMessage(message);
      },
      messageAction: (msgActionObj: Pubnub.MessageActionEvent) => {
        if (!channelIds.includes(msgActionObj.channel)) return;

        if (msgActionObj.data.type === 'Status') {
          setData((prevData) => {
            const newTimeToken = timeTokenToNumber(msgActionObj.data.messageTimetoken);
            const updatedData = prevData.map((item) => {
              if (isMessage(item) && newTimeToken - item.timetoken! < 3000)
                return { ...item, status: msgActionObj.data.value.toLowerCase() as MessageStatus };

              return item;
            });

            return updatedData;
          });
        }
      }
    };
    client.client.addListener(pubnubListener);

    return () => {
      client.client.removeListener(pubnubListener);
    };
  }, [client, query]);

  const addOrUpdateMessage = useCallback(
    (message: Message) => {
      setData((prevData) => {
        // handle duplicates for first motd where a template is applied to the message
        const templateDuplicate = prevData.findIndex(
          (c) =>
            isMessage(c) &&
            message.body != c.body &&
            c.body.includes(message.body) &&
            message.timetoken! - c.timetoken! < 5000
        );

        // handle duplicates when switching between message status'
        const index = prevData.findIndex(
          (c) =>
            isMessage(c) &&
            c.body === message.body &&
            c.senderUserId == message.senderUserId &&
            message.timetoken! - c.timetoken! < 5000
        );

        if (templateDuplicate >= 0) {
          // updating the status of the message we want to keep with the status of the message duplicate
          const data = prevData.filter((element) => element.timetoken !== message.timetoken) as Message[];
          data[templateDuplicate] = { ...data[templateDuplicate], status: message.status };

          return data;
        } else if (index >= 0) {
          const newData = [...prevData];
          newData.splice(index, 1, message);
          return newData;
        } else {
          return [...prevData, message];
        }
      });
    },
    [data]
  );

  const downloadFax = async (faxId: string) => {
    try {
      const blob = await callHistoryApi.getFax(faxId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = url;
      a.download = faxId;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log('Error downloading fax.', err);
    }
  };

  const contextValue = useMemo(
    () => ({
      data,
      automatedMessages,
      isLoading,
      hasMore,
      patientPhoneNumbers,
      setSearchByPhoneNumbers,
      listenToPhoneNumbers,
      loadMore,
      addOrUpdateMessage,
      downloadFax,
      setPatientId
    }),
    [
      data,
      automatedMessages,
      isLoading,
      hasMore,
      patientPhoneNumbers,
      setSearchByPhoneNumbers,
      listenToPhoneNumbers,
      loadMore,
      addOrUpdateMessage,
      downloadFax,
      setPatientId
    ]
  );

  return <CommuncationsContext.Provider value={contextValue}>{children}</CommuncationsContext.Provider>;
};
